import { formatInTimeZone } from 'date-fns-tz';

// Reference cities with their timezones
export const REFERENCE_TIMEZONES = [
  {
    city: 'UTC',
    country: null,
    timezone: 'UTC',
    flagImage: null,
    offset: 0,
    countries: ['Universal Time (no DST)']
  },
  {
    city: 'London',
    country: 'GBR',
    timezone: 'Europe/London',
    flagImage: '/flags/gb.png',
    offset: 0, // Winter: +0, Summer: +1
    countries: ['United Kingdom', 'Portugal (mainland)', 'Ireland']
  },
  {
    city: 'Berlin',
    country: 'DEU',
    timezone: 'Europe/Berlin',
    flagImage: '/flags/de.png',
    offset: 1, // Winter: +1, Summer: +2
    countries: ['Germany', 'France', 'Italy', 'Spain', 'Poland', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Czech Republic', 'Denmark', 'Sweden', 'Norway']
  },
  {
    city: 'Helsinki',
    country: 'FIN',
    timezone: 'Europe/Helsinki',
    flagImage: '/flags/fi.png',
    offset: 2, // Winter: +2, Summer: +3
    countries: ['Finland', 'Estonia', 'Latvia', 'Lithuania', 'Romania', 'Bulgaria', 'Greece', 'Ukraine']
  },
];

/**
 * Format a date as UTC with "UTC" suffix
 * @param date - Date string or Date object
 * @param formatString - Format string (default: 'dd MMM yyyy HH:mm')
 * @returns Formatted date string with " UTC" suffix
 */
export function formatUTC(date: string | Date, formatString: string = 'dd MMM yyyy HH:mm'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatInTimeZone(dateObj, 'UTC', formatString) + ' UTC';
}

/**
 * Format a date for a specific timezone
 * @param date - Date string or Date object
 * @param timezone - IANA timezone string
 * @param formatString - Format string (default: 'HH:mm')
 * @returns Formatted date string
 */
export function formatTimezone(date: string | Date, timezone: string, formatString: string = 'HH:mm'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatInTimeZone(dateObj, timezone, formatString);
}

/**
 * Get timezone abbreviation for a given timezone at a specific date
 * @param date - Date to check
 * @param timezone - IANA timezone string
 * @returns Timezone abbreviation (e.g., 'EST', 'CET')
 */
export function getTimezoneAbbr(date: Date, timezone: string): string {
  const formatted = formatInTimeZone(date, timezone, 'zzz');
  return formatted;
}

/**
 * Generate tooltip content with times in all reference cities
 * @param date - Date string or Date object
 * @returns Object with tooltip text for each city
 */
export function getTimezoneTooltip(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return REFERENCE_TIMEZONES.map(tz => {
    const time = formatInTimeZone(dateObj, tz.timezone, 'HH:mm');
    const offset = getCurrentOffset(tz.timezone);
    if (tz.city === 'UTC') {
      return `UTC: ${time}`;
    }
    return `${tz.city} (UTC${offset}): ${time}`;
  }).join('\n');
}

/**
 * Get current UTC offset for a timezone (accounting for DST)
 * @param timezone - IANA timezone string
 * @returns Offset string like "+1" or "+2"
 */
export function getCurrentOffset(timezone: string): string {
  // Special case for UTC timezone
  if (timezone === 'UTC') {
    return '+0';
  }

  const now = new Date();
  const formatted = formatInTimeZone(now, timezone, 'XXX'); // e.g., "+01:00" or "+02:00" or "Z"

  // Handle "Z" (which means UTC+0)
  if (formatted === 'Z') {
    return '+0';
  }

  const hours = parseInt(formatted.substring(1, 3), 10);
  return `+${hours}`;
}

/**
 * Get current time in all reference timezones
 * @returns Array of objects with city, time, flag, offset, and countries
 */
export function getCurrentTimesInZones(): Array<{
  city: string;
  country: string | null;
  time: string;
  flagImage: string | null;
  offset: string;
  countries: string[];
  timezone: string;
}> {
  const now = new Date();

  return REFERENCE_TIMEZONES.map(tz => ({
    city: tz.city,
    country: tz.country,
    time: formatInTimeZone(now, tz.timezone, 'HH:mm'),
    flagImage: tz.flagImage,
    offset: getCurrentOffset(tz.timezone),
    countries: tz.countries,
    timezone: tz.timezone,
  }));
}

/**
 * Get default ready time for MEDEVAC (current UTC + 36 hours, rounded to next 30 min)
 * @returns ISO string for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export function getDefaultReadyTimeUTC(): string {
  const now = new Date();

  // Add 36 hours
  now.setHours(now.getHours() + 36);

  // Round to next 30 minutes
  const minutes = now.getMinutes();
  if (minutes <= 30) {
    now.setMinutes(30);
  } else {
    now.setMinutes(0);
    now.setHours(now.getHours() + 1);
  }
  now.setSeconds(0);
  now.setMilliseconds(0);

  // Return as ISO string for datetime-local input (without Z to prevent browser conversion)
  // We want the user to input UTC time directly
  return formatInTimeZone(now, 'UTC', "yyyy-MM-dd'T'HH:mm");
}
